import React from 'react';
import RecraftGlyph from '../RecraftGlyph';
import EffectNodeGlyph from './EffectNodeGlyph';
import { SwordGlyph, ShieldGlyph, HeartGlyph } from './ResultPillIcons';
import {
  PROMOTED_STATUS_TAG_ICONS,
  PROMOTED_STATUS_WORDS,
} from './promotedEnemyStatusVocabulary';
import './statusWordGlyphs.css';

/* Inline status-resource glyphs for tooltip copy.

   Edward 2026-06-04: "whenever you say the word Ketone in the tooltip, use the
   same exact element as the real Ketone tag to reinforce visually what's doing
   what." So when a tooltip body mentions a resource that has a HUD status tag,
   we render that tag's exact glyph inline next to the word, in the tag's own
   color (pulled from CombatPlayerbar.css). Extend STATUS_WORDS as more tags
   gain tooltip mentions. */

const lacticIcon = (size) => (
  <svg
    className="status-word__icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 3h6" />
    <path d="M10 3v6.5L4.5 19.5a1.5 1.5 0 0 0 1.3 2.5h12.4a1.5 1.5 0 0 0 1.3-2.5L14 9.5V3" />
    <path d="M7 14h10" />
  </svg>
);

const streakIcon = (size) => (
  <svg
    className="status-word__icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 12c2-2.96 0-7-1-8 0 3.04-1.77 4.74-3 6-1.23 1.26-2 3.24-2 5a6 6 0 0 0 12 0c0-1.53-1.06-3.94-2-5-1.79 3-2.79 3-4 2z" />
  </svg>
);

// Enemy-threat status-tag glyphs — ported 1:1 from the matching Player*Tag.jsx
// HUD components so an inline mention in announce/tooltip copy reads identically
// to the real combat tag (same SVG, same color via the entry below). `svgIcon`
// is the shared 24x24 stroke wrapper every Player*Tag uses.
const svgIcon = (children) => (size, className = 'status-word__icon') => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const histamineIcon = svgIcon(<>
  <circle cx="12" cy="12" r="3.2" />
  <path d="M12 2.5v3" /><path d="M12 18.5v3" /><path d="M2.5 12h3" /><path d="M18.5 12h3" />
  <path d="M5.2 5.2l2.1 2.1" /><path d="M16.7 16.7l2.1 2.1" /><path d="M18.8 5.2l-2.1 2.1" /><path d="M7.3 16.7l-2.1 2.1" />
</>);
const depositIcon = svgIcon(<>
  <path d="M4 8h16" /><path d="M6 13h12" /><path d="M8.5 18h7" />
</>);
const delayedIcon = svgIcon(<>
  <circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5l3.2 2.2" />
</>);
const lipidIcon = svgIcon(
  <path d="M8 4C6.2 6.4 5 8.5 5 10.3a3 3 0 0 0 6 0C11 8.5 9.8 6.4 8 4Zm8 0c-1.8 2.4-3 4.5-3 6.3a3 3 0 0 0 6 0C19 8.5 17.8 6.4 16 4ZM12 12c-1.8 2.4-3 4.5-3 6.3a3 3 0 0 0 6 0c0-1.8-1.2-3.9-3-6.3Z" />
);
const plaqueIcon = svgIcon(
  <path d="M7.5 8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm8.5 2a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM8 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm9.5-1.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM10 11.5h4M10.4 15l3.2-2.6" />
);
const pressureIcon = svgIcon(<>
  <path d="M3.5 15a8.5 8.5 0 0 1 17 0" /><path d="M12 15l4.2-3.2" /><circle cx="12" cy="15" r="1.3" />
</>);
const cdiffIcon = svgIcon(<>
  <rect x="5.5" y="9" width="11" height="6" rx="3" />
  <path d="M9.5 12h0.01" /><path d="M13 12h0.01" /><path d="M16.5 12c1.6 0 1.4-2 3-2" />
</>);
const reservoirIcon = svgIcon(<>
  <path d="M7 3.5h10" /><path d="M8 3.5v14a3 3 0 0 0 3 3h2a3 3 0 0 0 3-3V3.5" /><path d="M8 11h8" />
  <path d="M11 15.5h0.01" /><path d="M14 14h0.01" />
</>);
const pulseIcon = svgIcon(
  <path d="M2 12h4l2-6 3 12 3-9 2 3h6" />
);
const freezeIcon = svgIcon(<>
  <path d="M12 2v20" /><path d="M3.34 7l17.32 10" /><path d="M20.66 7L3.34 17" />
  <path d="M12 5l-2.5 2M12 5l2.5 2M12 19l-2.5-2M12 19l2.5-2" />
</>);
const doseIcon = svgIcon(<>
  <rect x="3" y="8" width="18" height="8" rx="4" /><path d="M12 8v8" />
</>);
const confusionIcon = svgIcon(<>
  <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
  <path d="M12 9.5a2.5 2.5 0 1 1 -2.5 2.5" /><path d="M12 6.5a5.5 5.5 0 1 1 -5.5 5.5" />
</>);
const seizureIcon = svgIcon(
  <path d="M2 12h3l2-5 3 10 2.5-7 2 4 2-2h3.5" />
);
const protocolIcon = svgIcon(<>
  <rect x="3" y="9" width="6" height="6" rx="1.5" /><path d="M10.5 12h3" /><rect x="15" y="9" width="6" height="6" rx="1.5" />
</>);
const toxicityIcon = svgIcon(<>
  <path d="M3 17a9 9 0 0 1 18 0" /><path d="M12 17l3-5" /><path d="M11 8.2h2" />
</>);
const toleranceIcon = svgIcon(<>
  <path d="M12 3a6 6 0 0 1 6 6c0 4-3 6-6 6s-6-2-6-6a6 6 0 0 1 6-6z" /><path d="M12 15v6" /><path d="M9 21h6" />
</>);

// Player-side augment + HTN-meter status glyphs — ported 1:1 from the matching
// Player*Tag.jsx HUD components (all-statuses-as-chips, Edward 2026-07-12) so a
// chip or inline mention of these reads identically to the real HUD tag.
const cytokineTagIcon = svgIcon(<>
  <path d="M12 3v5" /><path d="M8 5l4 3 4-3" /><path d="M6 12h12" /><path d="M8 19l4-3 4 3" /><path d="M12 16v5" /><circle cx="12" cy="12" r="3" />
</>);
const immuneTagIcon = svgIcon(<>
  <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z" /><path d="M12 7v5l3 2" />
</>);
const endOrganTagIcon = svgIcon(<>
  <path d="M12 20s-6.5-4.2-6.5-9A3.2 3.2 0 0 1 12 7.5 3.2 3.2 0 0 1 18.5 11c0 4.8-6.5 9-6.5 9Z" /><path d="M12 9l-1.8 3.2h3L11.8 15" />
</>);
const pressureStackTagIcon = svgIcon(<>
  <path d="M4 15a8 8 0 0 1 16 0" /><path d="M12 15l4-5" /><circle cx="12" cy="15" r="1.3" fill="currentColor" stroke="none" />
</>);
const htnPulseTagIcon = svgIcon(<>
  <path d="M19.5 13.572l-7.5 7.428l-2.896 -2.868m-6.117 -8.104a5 5 0 0 1 9.013 -3.022a5 5 0 1 1 7.5 6.572" /><path d="M3 13h2l2 3l2 -6l1 3h3" />
</>);
const lacticTagIcon = svgIcon(<>
  <path d="M9 3h6" /><path d="M10 3v6.5L4.5 19.5a1.5 1.5 0 0 0 1.3 2.5h12.4a1.5 1.5 0 0 0 1.3-2.5L14 9.5V3" /><path d="M7 14h10" />
</>);
const streakTagIcon = svgIcon(
  <path d="M12 12c2-2.96 0-7-1-8 0 3.04-1.77 4.74-3 6-1.23 1.26-2 3.24-2 5a6 6 0 0 0 12 0c0-1.53-1.06-3.94-2-5-1.79 3-2.79 3-4 2z" />
);
// Recraft-glyph statuses (diuresis / glucagon / empagliflozin / potassium) render
// the card's own recraft icon, matching the "card's own icon" status convention.
const recraftTagIcon = (slug) => (size, className = 'status-word__icon') => (
  <RecraftGlyph slug={slug} size={size} className={className} />
);

const vasodilationTagIcon = svgIcon(<>
  {/* A vessel bowing OPEN — the same two-wall mark PlayerVasodilationTag and the
      KcStatusPills VasodilationGlyph draw, so the prose mention, the announce pill
      and the HUD tag are one image. */}
  <path d="M3 7c4 0 4.5-2.5 9-2.5S17 7 21 7" />
  <path d="M3 17c4 0 4.5 2.5 9 2.5s5-2.5 9-2.5" />
</>);

// Shared glyph factories (size, className) keyed by status slug — reused by the
// inline TRUE-tag renderer (statusTags.jsx) so the announce pills use the exact
// same SVG as the HUD tag, just with the pill's icon className.
export const STATUS_TAG_ICONS = {
  lipid: lipidIcon, plaque: plaqueIcon, histamine: histamineIcon, deposit: depositIcon,
  delayed: delayedIcon, pressure: pressureIcon, cdiff: cdiffIcon, reservoir: reservoirIcon,
  pulse: pulseIcon, freeze: freezeIcon, dose: doseIcon, confusion: confusionIcon,
  seizure: seizureIcon, sequence: protocolIcon, toxica: toxicityIcon, tolerance: toleranceIcon,
  // Player-side augments + HTN meters (all-statuses-as-chips, 2026-07-12)
  cytokine: cytokineTagIcon, immune: immuneTagIcon, endOrgan: endOrganTagIcon,
  pressureStack: pressureStackTagIcon, htnPulse: htnPulseTagIcon, lactic: lacticTagIcon,
  streak: streakTagIcon, glycogen: recraftTagIcon('glucagon'),
  ketone: recraftTagIcon('empagliflozin'), diuresis: recraftTagIcon('diuresis'),
  potassium: recraftTagIcon('potassium'), vasodilation: vasodilationTagIcon,
  ...PROMOTED_STATUS_TAG_ICONS,
};

// Keystone references in tooltip copy render as the keystone's NODE GLYPH inline
// (the same SVG used in the skill tree / combat rail), in the keystone's accent —
// REPLACING the word entirely (Edward 2026-06-05: "don't abbreviate Vanc, use the
// keystone node SVG in place"). `replace: true` → glyph only, no trailing text;
// `title` keeps the name accessible. Matches full names + the safe abbreviations
// ("vanc"/"adali"); the bare "met" is NOT matched (too collision-prone) — copy is
// de-abbreviated to "Metformin" at the source instead.
const ksEntry = (slug, color, title) => ({
  color,
  replace: true,
  title,
  glyph: (s) => <EffectNodeGlyph slug={slug} size={s} className="status-word__icon" />,
});

// keyword (lowercased) → { color, glyph(size) }. Colors mirror the matching
// Player*Tag in CombatPlayerbar.css so the inline mention reads identically.
export const STATUS_WORDS = {
  ketone: { color: 'rgba(167, 139, 250, 0.95)', glyph: (s) => <RecraftGlyph slug="empagliflozin" size={s} className="status-word__icon" /> },
  ketones: { color: 'rgba(167, 139, 250, 0.95)', glyph: (s) => <RecraftGlyph slug="empagliflozin" size={s} className="status-word__icon" /> },
  glycogen: { color: 'rgba(45, 212, 160, 0.95)', glyph: (s) => <RecraftGlyph slug="glucagon" size={s} className="status-word__icon" /> },
  lactic: { color: 'rgb(252, 165, 165)', glyph: lacticIcon },
  streak: { color: 'rgb(251, 146, 60)', glyph: streakIcon },
  // Enemy-inflicted status threats (mid-boss pools). Colors mirror the matching
  // Player*Tag in CombatPlayerbar.css so the inline mention reads identically to
  // the real HUD tag. Used by the pre-combat announce "how it attacks" line.
  lipid: { color: 'rgb(251, 191, 36)', glyph: lipidIcon },
  plaque: { color: 'rgb(244, 63, 94)', glyph: plaqueIcon },
  plaques: { color: 'rgb(244, 63, 94)', glyph: plaqueIcon },
  histamine: { color: 'rgb(244, 114, 182)', glyph: histamineIcon },
  deposit: { color: 'rgb(167, 139, 250)', glyph: depositIcon },
  deposits: { color: 'rgb(167, 139, 250)', glyph: depositIcon },
  delayed: { color: 'rgb(45, 212, 191)', glyph: delayedIcon },
  // Multi-word key on purpose: matches only Pestil's real "resistance pressure"
  // mechanic, NOT the metaphorical "pressure" in simul-pool flavor copy (which
  // must stay plain). renderStatusText's regex handles the space.
  'resistance pressure': { color: 'rgb(245, 158, 11)', glyph: pressureIcon },
  cdiff: { color: 'rgb(132, 204, 22)', glyph: cdiffIcon },
  reservoir: { color: 'rgb(168, 85, 247)', glyph: reservoirIcon },
  pulse: { color: 'rgb(74, 222, 128)', glyph: pulseIcon },
  vasodilation: { color: 'rgb(96, 165, 250)', glyph: vasodilationTagIcon },
  freeze: { color: 'rgb(56, 189, 248)', glyph: freezeIcon },
  levodopa: { color: 'rgb(148, 163, 184)', glyph: doseIcon },
  confusion: { color: 'rgb(192, 132, 252)', glyph: confusionIcon },
  seizure: { color: 'rgb(168, 85, 247)', glyph: seizureIcon },
  protocol: { color: 'rgb(244, 114, 182)', glyph: protocolIcon },
  toxicity: { color: 'rgb(251, 146, 60)', glyph: toxicityIcon },
  tolerance: { color: 'rgb(245, 158, 11)', glyph: toleranceIcon },
  // Core mechanic vocabulary (tooltip canon 2026-06-11) — the SAME glyphs the
  // result pills / GameTip MECH map use, so a prose mention of "damage" reads
  // identically to a structured `⚔ 18` stat row. Word stays visible (glyph +
  // word, no replace). Colors mirror GameTip/glyphs.jsx MECH — keep in sync.
  damage: { color: 'rgb(248, 113, 113)', glyph: (s) => <SwordGlyph size={s} className="status-word__icon" /> },
  block: { color: 'rgb(148, 163, 184)', glyph: (s) => <ShieldGlyph size={s} className="status-word__icon" /> },
  blocks: { color: 'rgb(148, 163, 184)', glyph: (s) => <ShieldGlyph size={s} className="status-word__icon" /> },
  blocked: { color: 'rgb(148, 163, 184)', glyph: (s) => <ShieldGlyph size={s} className="status-word__icon" /> },
  heal: { color: 'rgb(74, 222, 128)', glyph: (s) => <HeartGlyph size={s} className="status-word__icon" /> },
  heals: { color: 'rgb(74, 222, 128)', glyph: (s) => <HeartGlyph size={s} className="status-word__icon" /> },
  healing: { color: 'rgb(74, 222, 128)', glyph: (s) => <HeartGlyph size={s} className="status-word__icon" /> },
  healed: { color: 'rgb(74, 222, 128)', glyph: (s) => <HeartGlyph size={s} className="status-word__icon" /> },
  hp: { color: 'rgb(74, 222, 128)', glyph: (s) => <HeartGlyph size={s} className="status-word__icon" /> },
  round: { color: 'rgb(45, 212, 191)', glyph: delayedIcon },
  rounds: { color: 'rgb(45, 212, 191)', glyph: delayedIcon },
  // Keystone node glyphs (replace the word).
  vancomycin: ksEntry('vancomycin', '#ef4444', 'Vancomycin'),
  vanc: ksEntry('vancomycin', '#ef4444', 'Vancomycin'),
  metformin: ksEntry('metformin', '#34d399', 'Metformin'),
  adalimumab: ksEntry('adalimumab', '#60a5fa', 'Adalimumab'),
  adali: ksEntry('adalimumab', '#60a5fa', 'Adalimumab'),
  // Shared production-family vocabulary wins over any stale local duplicate.
  ...PROMOTED_STATUS_WORDS,
};

// Build the alternation once. Longest-first so "ketones" wins over "ketone".
const WORDS_RE = new RegExp(
  `\\b(${Object.keys(STATUS_WORDS).sort((a, b) => b.length - a.length).join('|')})\\b`,
  'gi',
);

/* Render a copy string with inline status glyphs. Returns a React fragment.
   Each matched resource word becomes `<glyph/> word` styled in the tag color. */
export function renderStatusText(text, { iconSize = 13 } = {}) {
  if (!text) return null;
  const parts = String(text).split(WORDS_RE);
  return (
    <>
      {parts.map((part, i) => {
        const entry = STATUS_WORDS[part.toLowerCase()];
        if (!entry) return <React.Fragment key={i}>{part}</React.Fragment>;
        // Keystone tokens: render the node glyph IN PLACE of the word (no text).
        if (entry.replace) {
          return (
            <span
              key={i}
              className="status-word status-word--glyph-only"
              style={{ color: entry.color }}
              aria-label={entry.title || part}
            >
              {entry.glyph(iconSize + 3)}
            </span>
          );
        }
        return (
          <span key={i} className="status-word" style={{ color: entry.color }}>
            {entry.glyph(iconSize)}
            {part}
          </span>
        );
      })}
    </>
  );
}
